import { InjectionToken } from '@angular/core';

/**
 * Colour/typography contract for the sandbox usage dashboard.
 * Each app provides its own values via {@link SANDBOX_DASHBOARD_THEME}; nothing in the
 * component hard-codes brand colours, so the same dashboard can be re-skinned per app.
 */
export interface SandboxDashboardTheme {
  /** Active segment, primary actions, links, focus outline. */
  accent: string;
  /** Text/icon colour on top of `accent`. */
  accentContrast: string;
  /** Soft accent fill: selected rows, hovers, small badges. */
  accentSoft: string;
  /** Series colour for CPU (chart bars, legend dot, CPU figures). */
  cpuColor: string;
  /** Series colour for GPU. */
  gpuColor: string;
  /** Bar / legend colour for CPU in the chart (can be lighter than the text colour `cpuColor`). */
  chartCpuColor: string;
  /** Bar / legend colour for GPU in the chart. */
  chartGpuColor: string;
  textPrimary: string;
  textSecondary: string;
  /** Dashboard, section and card titles. */
  headingColor: string;
  border: string;
  /** Card / panel background. */
  surface: string;
  /** Stats strip, table header and inactive segment background. */
  surfaceMuted: string;
  /** Dashboard canvas behind the cards. */
  background: string;
  success: string;
  successSoft: string;
  warning: string;
  warningSoft: string;
  danger: string;
  dangerSoft: string;
  chartGridLine: string;
  /** Dropdown trigger fill (period, sort, rows per page). */
  controlBackground: string;
  controlBorder: string;
  /** Selected value text inside a dropdown trigger. */
  controlValueText: string;
  /** Focus/open ring around a dropdown trigger. */
  controlFocusRing: string;
  /** Border of the pill (multi-select) filter trigger. */
  filterBorder: string;
  /** Selected-value pill inside the filter trigger. */
  chipBackground: string;
  chipText: string;
  tableHeaderBackground: string;
  tableHeaderText: string;
  /** Outer border of the users table. */
  tableBorder: string;
  tableRowDivider: string;
  /** Outline of page / navigation buttons in the users pager. */
  pagerBorder: string;
  /** Fill of the current page button (text uses `accentContrast`). */
  pagerActiveBackground: string;
  /** Corner radius for cards and panels. */
  radius: string;
  /** Used by both CSS and the chart canvas, so it must be a real font stack (not `inherit`). */
  fontFamily: string;
}

/** Per-app theme for the sandbox usage dashboard. */
export const SANDBOX_DASHBOARD_THEME =
  new InjectionToken<SandboxDashboardTheme>('SANDBOX_DASHBOARD_THEME');

/** Fallback (neutral blue) theme when an app does not provide one. */
export const DEFAULT_SANDBOX_DASHBOARD_THEME: SandboxDashboardTheme = {
  accent: '#1d5fd6',
  accentContrast: '#ffffff',
  accentSoft: '#e8f0fd',
  cpuColor: '#2f6fe4',
  gpuColor: '#14a39a',
  chartCpuColor: '#2f6fe4',
  chartGpuColor: '#14a39a',
  textPrimary: '#111827',
  textSecondary: '#6b7280',
  headingColor: '#111827',
  border: '#e5e7eb',
  surface: '#ffffff',
  surfaceMuted: '#f8fafc',
  background: '#f9fafb',
  success: '#047857',
  successSoft: '#d1fae5',
  warning: '#b45309',
  warningSoft: '#fef3c7',
  danger: '#b91c1c',
  dangerSoft: '#fee2e2',
  chartGridLine: '#eef0f3',
  controlBackground: '#ffffff',
  controlBorder: '#d1d5db',
  controlValueText: '#1d5fd6',
  controlFocusRing: 'rgba(29, 95, 214, 0.12)',
  filterBorder: '#9ca3af',
  chipBackground: '#1d5fd6',
  chipText: '#ffffff',
  tableHeaderBackground: '#f8fafc',
  tableHeaderText: '#111827',
  tableBorder: '#e5e7eb',
  tableRowDivider: '#eef0f3',
  pagerBorder: '#d1d5db',
  pagerActiveBackground: '#1d5fd6',
  radius: '12px',
  fontFamily:
    "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
};

/** Maps a theme to the `--sbd-*` CSS custom properties consumed by the component styles. */
export function sandboxDashboardThemeVars(
  theme: SandboxDashboardTheme,
): Record<string, string> {
  return {
    '--sbd-accent': theme.accent,
    '--sbd-accent-contrast': theme.accentContrast,
    '--sbd-accent-soft': theme.accentSoft,
    '--sbd-cpu': theme.cpuColor,
    '--sbd-gpu': theme.gpuColor,
    '--sbd-chart-cpu': theme.chartCpuColor,
    '--sbd-chart-gpu': theme.chartGpuColor,
    '--sbd-text': theme.textPrimary,
    '--sbd-text-secondary': theme.textSecondary,
    '--sbd-heading': theme.headingColor,
    '--sbd-border': theme.border,
    '--sbd-surface': theme.surface,
    '--sbd-surface-muted': theme.surfaceMuted,
    '--sbd-background': theme.background,
    '--sbd-success': theme.success,
    '--sbd-success-soft': theme.successSoft,
    '--sbd-warning': theme.warning,
    '--sbd-warning-soft': theme.warningSoft,
    '--sbd-danger': theme.danger,
    '--sbd-danger-soft': theme.dangerSoft,
    '--sbd-control-bg': theme.controlBackground,
    '--sbd-control-border': theme.controlBorder,
    '--sbd-control-value': theme.controlValueText,
    '--sbd-control-ring': theme.controlFocusRing,
    '--sbd-filter-border': theme.filterBorder,
    '--sbd-chip-bg': theme.chipBackground,
    '--sbd-chip-text': theme.chipText,
    '--sbd-table-header-bg': theme.tableHeaderBackground,
    '--sbd-table-header-text': theme.tableHeaderText,
    '--sbd-table-border': theme.tableBorder,
    '--sbd-table-divider': theme.tableRowDivider,
    '--sbd-pager-border': theme.pagerBorder,
    '--sbd-pager-active-bg': theme.pagerActiveBackground,
    '--sbd-radius': theme.radius,
    '--sbd-font-family': theme.fontFamily,
  };
}
